import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, Input, OnInit} from '@angular/core';

import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu'
import {NbButtonModule, NbSpinnerModule, NbTooltipModule} from '@nebular/theme'
import {TuiIcon} from '@taiga-ui/core'
import {isObservable, Observable} from 'rxjs';
import {finalize} from 'rxjs/operators';

export interface ActionButton {
  icon: string;
  title: string;
  handler?: (row) => void | Observable<unknown>;
  href?: string | ((row) => string);
  target?: string;
  visible?: boolean | ((row) => boolean);
}

@Component({
  selector: 'app-menu-actions',
  templateUrl: './actions.component.html',
  styles: `
    :host.icons {
      display: flex;
      gap: 4px 15px;
    }

    .is-loading {
      opacity: 0.5;
      transition: opacity 0.15s;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatMenu,
    MatMenuItem,
    NbButtonModule,
    TuiIcon,
    MatMenuTrigger,
    NbTooltipModule,
    NbSpinnerModule
  ]
})
export class ActionsComponent implements OnInit {
  @Input() row;
  @Input() config: ActionButton[];
  @Input() mode: string = 'menu';

  @HostBinding('class')
  get cssClass(): string[] {
    return [this.mode];
  }

  actions: ActionButton[] = [];
  loadingActions = new Set<number>();

  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.actions = this.buildActions();
  }

  onActionClick(action: ActionButton, index: number): void {
    if (!action.handler) {
      return;
    }
    if (this.loadingActions.has(index)) {
      return;
    }

    const result = action.handler(this.row);
    if (!isObservable(result)) {
      return;
    }

    this.loadingActions.add(index);
    this.cdr.detectChanges();

    result
      .pipe(
        finalize(() => {
          this.loadingActions.delete(index);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  buildActions() {
    return this.config
      .map(config => {
        const visible = typeof config.visible === 'function' ? config.visible(this.row) : config.visible ?? true;
        if (!visible) {
          return null;
        }

        const href = typeof config.href === 'function' ? config.href(this.row) : config.href ?? undefined;

        return {
          icon: config.icon,
          title: config.title,
          handler: config.handler,
          href,
          target: config.target
        };
      })
      .filter(action => action !== null) as ActionButton[];
  }

}
